import React from 'react';
import { Spinner } from 'react-bootstrap';
import { ErrorModal } from '~/components/Errors/ErrorModal';
import { DatePicker } from '~/components/Form';
import { ClientContext, patientServiceEvents, ServicesContext } from '~/services';
import { enumValue, useServiceSubscribe } from '~/util';
import { useRoleActions } from '~/util/useRoleActions';
import { moment } from '~/vendorConfigs/moment';

interface DateOfVisitProps {
    patientId: number;
    submission?: null | PatientSubmissionType;
    disabled?: boolean;
}
function DateOfVisit({ patientId, submission = null, disabled = false }: DateOfVisitProps) {
    const servicesContext = React.useContext(ServicesContext);
    const clientContext = React.useContext(ClientContext);
    const canEdit = useRoleActions('allow_edit_date_of_visit');
    const [dateOfVisit, setDov] = React.useState<null | Date>(null);
    const [hasError, setShowError] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');

    const storeDov = React.useCallback(
        async (date: null | string) => {
            const params = {
                patient: {
                    id: patientId,
                },
                gap_type: enumValue(['gap', 'type', 'Risk Gap']),
                dov: date,
                ...(submission?.updated_at && { updated_at: submission.updated_at }),
            };
            setSubmitting(true);
            try {
                await servicesContext.patient.addSubmission(params);
            } catch (e) {
                // Empty catch
            } finally {
                setSubmitting(false);
            }
        },
        [patientId, servicesContext.patient, submission]
    );

    React.useEffect(() => {
        function convertDov(dov: string) {
            const date = new Date(dov.replace(/-/g, '/'));
            if (date instanceof Date && !Number.isNaN(date.getTime())) {
                return date;
            }
            // If we got an invalid date, reset the data to have null
            storeDov(null);
            return null;
        }
        if (submission?.dov) {
            setDov(convertDov(submission.dov));
        } else {
            setDov(null);
        }
    }, [storeDov, submission]);

    const autoDov = React.useCallback(() => {
        // Only auto set the DOV if it is currently empty
        if (dateOfVisit !== null) return;
        // Only auto set in current year
        if (clientContext.isPriorYearSelected) return;
        const today = moment().format('YYYY-MM-DD');
        storeDov(today);
    }, [clientContext.isPriorYearSelected, dateOfVisit, storeDov]);

    useServiceSubscribe(patientServiceEvents.risk_gap_response, autoDov);

    function handleDateChange(value: null | Date) {
        if (value === null) {
            storeDov(null);
            return;
        }
        const momentVal = moment(value);
        const now = moment();
        const minBound = moment().subtract(120, 'years');

        if (momentVal < minBound) {
            // If value is way in the past, it's because our datepicker is updating too soon
            // ignore these dates
            return;
        }

        if (now < momentVal) {
            // Error - Date of Visit after current date
            setError('This eAttestation cannot be submitted until the patient visit has occurred.');
            setShowError(true);
        } else if (momentVal < now.subtract(1, 'years')) {
            // Error - Date of Visit more than 1 year before current date
            setError(
                'The date of the patient’s visit occurred 1 year or more in the past, are you sure you would like to submit this date as part of the current eAttestation?'
            );
            setShowError(true);
        }

        storeDov(momentVal.format('YYYY-MM-DD'));
    }

    const acceptableDateRange = React.useMemo(() => {
        const reportYear = moment().set('year', clientContext.reportYear);
        const endOfReportYear = reportYear.clone().endOf('year');
        return [
            reportYear.clone().startOf('year').toDate(),
            endOfReportYear.toDate(),
            clientContext.isPriorYearSelected ? endOfReportYear.toDate() : undefined,
        ];
    }, [clientContext.isPriorYearSelected, clientContext.reportYear]);

    return (
        <>
            <label htmlFor="dateofvisit" className="form-label me-2">
                Date of Visit
            </label>
            <div
                className="d-inline"
                title={
                    !canEdit
                        ? 'You don’t have permission to change the Date of Visit on this record.'
                        : undefined
                }
            >
                <DatePicker
                    key={clientContext.reportYear}
                    name="dateofvisit"
                    id="dateofvisit"
                    value={dateOfVisit}
                    onChange={handleDateChange}
                    disabled={disabled || !canEdit}
                    minDate={acceptableDateRange[0]}
                    maxDate={acceptableDateRange[1]}
                    defaultActiveStartDate={acceptableDateRange[2]}
                />
            </div>
            {submitting && <Spinner animation="border" size="sm" className="ms-2" />}
            {hasError && (
                <ErrorModal
                    show={hasError}
                    handleClose={() => setShowError(false)}
                    title="Date of Visit Error"
                    description={error}
                />
            )}
        </>
    );
}

export { DateOfVisit };
